import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import formatDate from "date-fns/format";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import parseISO from "date-fns/parseISO";
import { STAT_TYPE_ATTRS, STAT_TYPE_LABELS } from "./performance-stat-types";
import PerformanceChartEmptyState from "./performance-chart-empty-state";

const propTypes = {
  series: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string.isRequired,
      impressions: PropTypes.number,
      clicks: PropTypes.number,
      videoStarts: PropTypes.number,
      products: PropTypes.arrayOf(
        PropTypes.shape({
          key: PropTypes.string.isRequired,
          impressions: PropTypes.number,
          clicks: PropTypes.number,
          videoStarts: PropTypes.number
        })
      )
    })
  ),
  products: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired
    })
  ),
  statType: PropTypes.oneOf(["views", "clicks", "plays"]).isRequired,
  cumulativeViewMode: PropTypes.bool.isRequired,
  trackPerformanceClick: PropTypes.func
};

const CHART_LINE_COLOR = "#2a7bcf";
const CHART_AREA_FILL_COLOR = "#bfd7f1";

const PerformanceChart = ({
  series = [],
  products = [],
  statType,
  cumulativeViewMode,
  trackPerformanceClick = () => {}
}) => {
  const [hiddenSeriesKeys, setHiddenSeriesKeys] = useState(new Set());

  const parseLocalDate = dateString => {
    if (!dateString) return null;
    return parseISO(dateString);
  };

  const toggleSeriesVisibility = ({ dataKey }) => {
    setHiddenSeriesKeys(previousHiddenKeys => {
      const nextHiddenKeys = new Set(previousHiddenKeys);
      if (nextHiddenKeys.has(dataKey)) {
        nextHiddenKeys.delete(dataKey);
      } else {
        nextHiddenKeys.add(dataKey);
      }
      return nextHiddenKeys;
    });
    trackPerformanceClick();
  };

  const dataKey = STAT_TYPE_ATTRS[statType];
  const label = STAT_TYPE_LABELS[statType];
  const axisLabelPrefix = cumulativeViewMode ? "Total" : "Daily";
  const isSplitByProduct = products.length > 1;
  const ChartComponent = cumulativeViewMode ? AreaChart : LineChart;
  const SeriesComponent = cumulativeViewMode ? Area : Line;
  const formatAxisDate = date => formatDate(parseLocalDate(date), "MMM d");

  const toProductSplitRow = day => {
    const statsByKey = new Map((day.products || []).map(stat => [stat.key, stat]));
    const row = { date: day.date };
    products.forEach(product => {
      const productStat = statsByKey.get(product.key);
      row[product.key] = productStat ? productStat[dataKey] : 0;
    });
    return row;
  };

  const chartData = useMemo(
    () => (isSplitByProduct ? series.map(day => toProductSplitRow(day)) : series),
    [series, products, dataKey, isSplitByProduct]
  );

  const chartSeries = isSplitByProduct
    ? products.map(product => ({ key: product.key, name: product.label, stroke: product.color }))
    : [{ key: dataKey, name: label, stroke: CHART_LINE_COLOR }];

  const getAreaProps = chartSeriesItem =>
    isSplitByProduct
      ? { stackId: "products", fill: chartSeriesItem.stroke, fillOpacity: 0.4 }
      : { fill: CHART_AREA_FILL_COLOR, fillOpacity: 0.6 };

  if (series.length === 0) {
    return (
      <div className="performance-chart">
        <PerformanceChartEmptyState
          label={label}
          cumulativeViewMode={cumulativeViewMode}
          legendItems={chartSeries}
        />
      </div>
    );
  }

  return (
    <div className="performance-chart">
      <ResponsiveContainer width="100%" height={500}>
        <ChartComponent data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
          <CartesianGrid vertical={false} />
          <XAxis dataKey="date" tickFormatter={formatAxisDate} tickLine={false} />
          <YAxis
            allowDecimals={false}
            tickLine={false}
            label={{
              value: `${axisLabelPrefix} ${label}`,
              angle: -90,
              position: "insideLeft"
            }}
          />
          <Tooltip labelFormatter={formatAxisDate} contentStyle={{ fontSize: 13 }} />
          <Legend
            verticalAlign="bottom"
            align="left"
            iconType={cumulativeViewMode ? "square" : "line"}
            wrapperStyle={{ fontSize: 13, paddingTop: 8, cursor: "pointer" }}
            onClick={toggleSeriesVisibility}
          />
          {chartSeries.map(chartSeriesItem => (
            <SeriesComponent
              key={chartSeriesItem.key}
              type="monotone"
              dataKey={chartSeriesItem.key}
              name={chartSeriesItem.name}
              stroke={chartSeriesItem.stroke}
              strokeWidth={2}
              isAnimationActive={false}
              hide={hiddenSeriesKeys.has(chartSeriesItem.key)}
              {...(cumulativeViewMode ? getAreaProps(chartSeriesItem) : {})}
            />
          ))}
        </ChartComponent>
      </ResponsiveContainer>
    </div>
  );
};

PerformanceChart.propTypes = propTypes;

export default PerformanceChart;
